import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowLeft, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { usePageSEO } from "@/hooks/use-seo";

const PaymentSuccess: React.FC = () => {
  usePageSEO("Payment Successful – Zoocertica", "Your payment has been processed successfully.", "/payment-success");
  
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);
  const { toast } = useToast();
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        toast({
          title: "Invalid Payment",
          description: "No payment session found.",
          variant: "destructive",
        });
        setVerifying(false);
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('verify-payment', {
          body: { sessionId },
        });

        if (error) throw error;
        
        setPaymentDetails(data);
        
        if (data.status === 'paid') {
          toast({
            title: "Payment Successful!",
            description: "Your payment has been processed successfully.",
          });
        } else {
          toast({
            title: "Payment Issue",
            description: "There was an issue with your payment. Please contact support@zoocertica.com for assistance.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Payment verification error:", error);
        toast({
          title: "Verification Error",
          description: "Unable to verify payment status. Please contact support@zoocertica.com for assistance.",
          variant: "destructive",
        });
      } finally {
        setVerifying(false);
      }
    };

    verifyPayment();
  }, [sessionId, toast]);

  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
            <h2 className="text-lg font-semibold">Verifying Payment...</h2>
            <p className="text-muted-foreground text-center mt-2">
              Please wait while we confirm your payment.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <CardTitle className="text-2xl text-green-700">Payment Successful!</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">
            Thank you for your purchase. Your payment has been processed successfully.
          </p>
          
          {paymentDetails?.order && (
            <div className="bg-muted p-4 rounded-lg text-left">
              <h3 className="font-semibold mb-2">Order Details:</h3>
              <p><strong>Product:</strong> {paymentDetails.order.product_name}</p>
              <p><strong>Amount:</strong> ${(paymentDetails.order.amount / 100).toFixed(2)}</p>
              <p><strong>Status:</strong> {paymentDetails.order.status}</p>
            </div>
          )}
          
          <div className="flex flex-col gap-2">
            <Button asChild>
              <Link to="/">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return to Home
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;